import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "space-y-4 p-4 sm:p-6 lg:p-8" }
const _hoisted_2 = { class: "flex items-center text-xl font-semibold text-gray-800" }
const _hoisted_3 = { class: "space-y-2" }
const _hoisted_4 = { class: "flex items-center space-x-4" }
const _hoisted_5 = {
  key: 1,
  class: "space-y-2"
}

import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import store from '@/store';
import { useFilterStore, useUiStore } from '@/stores';
import ContactService from '@/services/api/ContactService';
import { toastServiceError } from '@/services/core/notification';

import {
    ContactActions,
    ContactGetters,
    ContactModel,
    ContactServiceGetAllParams,
    SortMenuItem,
    Permission,
    ContactFilter,
} from '@/types';

import {
    ContactAuraPanel,
    ContactListItem,
    ContactListSort,
} from '@/views/pages/contact/components';

import { BookOpenIcon } from '@heroicons/vue/solid';
import ContactListFilters from './components/ContactListFilters.vue';

const showAskAuraButton = false;

const perPage = 10;


export default /*@__PURE__*/_defineComponent({
  __name: 'ContactPage',
  setup(__props) {

const route = useRoute();
const router = useRouter();

const filterStore = useFilterStore();
const uiStore = useUiStore();

const contacts = ref<ContactModel[]>([]);
const count = ref(0);
const currentPage = computed({
    get: () => store.getters[ContactGetters.PAGE],
    set: (value: number) => store.dispatch(ContactActions.SET_PAGE, value),
});

const search = computed({
    get: () => store.getters[ContactGetters.SEARCH],
    set: (value: string) => store.dispatch(ContactActions.SET_SEARCH, value),
});

const sortMenuSelected = computed<SortMenuItem[]>({
    get: () => store.getters[ContactGetters.SORT],
    set: (value: SortMenuItem[]) => store.dispatch(ContactActions.SET_SORT, value),
});

const levelFilter = computed(() => filterStore.level);
const healthFilter = computed<ContactFilter>({
    get: () => store.getters[ContactGetters.FILTER],
    set: (value) => store.dispatch(ContactActions.SET_FILTER, value),
});

const orderBy = computed(() => sortMenuSelected.value.map((s) => s.direction + s.key).join(','));

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const getParams = (): ContactServiceGetAllParams => ({
    page: currentPage.value,
    per_page: perPage,
    order_by: orderBy.value,
    search: search.value,
});

const fetchContacts = async () => {
    await setLoading(true);
    try {
        const { data, headers } = await ContactService.getAll<ContactModel[]>({
            params: getParams(),
        });
        contacts.value = data;
        count.value = parseInt(headers['x-pagination-count']);
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const handleRemoveSortClick = (item: SortMenuItem) => {
    const index = sortMenuSelected.value.indexOf(item);
    if (index > -1) sortMenuSelected.value.splice(index, 1);
};

watch([currentPage], () => {
    fetchContacts();
});

watch([levelFilter, search, orderBy, healthFilter], () => {
    if (currentPage.value === 1) fetchContacts();
    else currentPage.value = 1;
});

if (route.query.clearStateOnSetup) {
    search.value = '';
    sortMenuSelected.value = [];
    currentPage.value = 1;
    router.replace({ query: {} });
}

onMounted(fetchContacts);

return (_ctx: any,_cache: any) => {
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_router_link = _resolveComponent("router-link")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiListSearch = _resolveComponent("UiListSearch")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_UiSortMenuBadge = _resolveComponent("UiSortMenuBadge")!
  const _component_UiPagination = _resolveComponent("UiPagination")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_2, [
          _createVNode(_unref(BookOpenIcon), {
            class: "mr-1 h-6 w-6 text-gray-500",
            "aria-hidden": "true"
          }),
          _createTextVNode(" " + _toDisplayString(_ctx.$t('core.entity.Contacts')), 1)
        ])
      ]),
      actions: _withCtx(() => [
        showAskAuraButton
          ? (_openBlock(), _createBlock(_unref(ContactAuraPanel), { key: 0 }))
          : _createCommentVNode("", true),
        (_ctx.$can(_unref(Permission).CREATE_CONTACT))
          ? (_openBlock(), _createBlock(_component_router_link, {
              key: 1,
              to: { name: 'CreateContact' }
            }, {
              default: _withCtx(() => [
                _createVNode(_component_UiButton, {
                  variant: "primary",
                  "text-variant": "white"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Add')), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_component_UiPanel, {
        class: "flex px-4 py-2",
        "no-padding": ""
      }, {
        default: _withCtx(() => [
          _createVNode(_component_UiListSearch, {
            modelValue: search.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event)),
            placeholder: _ctx.$t('contact.filters.search'),
            class: "flex-1"
          }, null, 8, ["modelValue", "placeholder"]),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_unref(ContactListSort), {
              modelValue: sortMenuSelected.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((sortMenuSelected).value = $event))
            }, null, 8, ["modelValue"]),
            _createVNode(ContactListFilters, {
              modelValue: healthFilter.value,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((healthFilter).value = $event))
            }, null, 8, ["modelValue"])
          ])
        ]),
        _: 1
      }),
      (sortMenuSelected.value.length)
        ? (_openBlock(), _createBlock(_component_UiPanel, {
            key: 0,
            class: "space-x-2"
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortMenuSelected.value, (selected) => {
                return (_openBlock(), _createBlock(_component_UiSortMenuBadge, {
                  key: selected.key,
                  item: selected,
                  onRemove: ($event: any) => (handleRemoveSortClick(selected))
                }, null, 8, ["item", "onRemove"]))
              }), 128))
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (contacts.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(contacts.value, (contact) => {
              return (_openBlock(), _createBlock(_unref(ContactListItem), {
                key: contact.id,
                "model-value": contact
              }, null, 8, ["model-value"]))
            }), 128))
          ]))
        : (_openBlock(), _createBlock(_component_UiPanel, {
            key: 2,
            class: "flex items-center justify-center"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('core.messages.noRecordsFound')), 1)
            ]),
            _: 1
          })),
      (contacts.value.length)
        ? (_openBlock(), _createBlock(_component_UiPagination, {
            key: 3,
            modelValue: currentPage.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((currentPage).value = $event)),
            count: count.value,
            perPage: perPage
          }, null, 8, ["modelValue", "count"]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})